import cn from 'classnames'
import React from 'react'
import { useFormControlContext } from './form-control-context'
import { typographyStyles } from '@grammarly/badger-ui'
import styles from './form.module.scss'
import { RequiredOrOptional, useFormContext } from './form-context'

interface LabelProps {
  children?: React.ReactNode
  className?: string

  /**
   * When using Label inside a FormControl group, the following props may be omitted.
   *
   * Explanation: When rendering an input inside a form, you can either:
   * - Render just the input (no label or error, this is not a common case if we are following GDS)
   * - Render the input as part of a FormControl group with other things like label and error text
   *
   * The props here should usually be passed in via FormControlContext and only exist as props on the input/label itself if it is used outside of a FormControl.
   * Props passed in to FormProvider should not be passed here as they are always accessible via the form context.
   */
  htmlFor?: string
  isRequired?: boolean
}

const getInputTypeIndicator = (isRequired?: boolean, requiredOrOptional?: RequiredOrOptional): string | null => {
  return requiredOrOptional === RequiredOrOptional.Required && isRequired
    ? '(Required)'
    : requiredOrOptional === RequiredOrOptional.Optional && !isRequired
      ? '(Optional)'
      : null
}

export const Label: React.FC<LabelProps> = ({ children, className, ...rest }) => {
  const { requiredOrOptionalIndicator } = useFormContext()
  const { idContext, isRequiredContext } = useFormControlContext()
  const { htmlFor = idContext, isRequired = isRequiredContext } = rest
  const inputType = getInputTypeIndicator(isRequired, requiredOrOptionalIndicator)

  return (
    <label className={cn(typographyStyles.C4, styles.label, className)} htmlFor={htmlFor}>
      {children}
      <span className={styles.indicator}> {inputType}</span>
    </label>
  )
}
